import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  schema?: Record<string, unknown>;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  schema,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
}: SEOProps) {
  const fullTitle = `${title} | 911爆料网`;

  useEffect(() => {
    if (typeof document === 'undefined') {
      return null;
    }

    document.title = fullTitle;

    const metaTags: { [key: string]: HTMLMetaElement } = {};
    const linkTags: { [key: string]: HTMLLinkElement } = {};

    const setMeta = (name: string, content: string, property?: string) => {
      const key = property ? `property:${property}` : `name:${name}`;
      if (!metaTags[key]) {
        const el = document.createElement('meta');
        if (property) {
          el.setAttribute('property', property);
        } else {
          el.setAttribute('name', name);
        }
        el.setAttribute('content', content);
        document.head.appendChild(el);
        metaTags[key] = el;
      } else {
        metaTags[key].setAttribute('content', content);
      }
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    if (canonicalUrl) {
      const key = 'canonical';
      if (!linkTags[key]) {
        const el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        el.setAttribute('href', canonicalUrl);
        document.head.appendChild(el);
        linkTags[key] = el;
      } else {
        linkTags[key].setAttribute('href', canonicalUrl);
      }
    }

    setMeta('og:title', fullTitle, 'og:title');
    setMeta('og:description', description, 'og:description');
    setMeta('og:type', ogType, 'og:type');
    if (canonicalUrl) setMeta('og:url', canonicalUrl, 'og:url');
    if (ogImage) setMeta('og:image', ogImage, 'og:image');
    setMeta('og:site_name', '911爆料网', 'og:site_name');
    setMeta('og:locale', 'zh_CN', 'og:locale');

    setMeta('twitter:card', 'summary_large_image', 'twitter:card');
    setMeta('twitter:title', fullTitle, 'twitter:title');
    setMeta('twitter:description', description, 'twitter:description');
    if (ogImage) setMeta('twitter:image', ogImage, 'twitter:image');

    if (articlePublishedTime) setMeta('article:published_time', articlePublishedTime, 'article:published_time');
    if (articleModifiedTime) setMeta('article:modified_time', articleModifiedTime, 'article:modified_time');
    if (articleAuthor) setMeta('article:author', articleAuthor, 'article:author');

    let scriptEl: HTMLScriptElement | null = null;
    if (schema) {
      scriptEl = document.createElement('script');
      scriptEl.type = 'application/ld+json';
      scriptEl.textContent = JSON.stringify(schema);
      document.head.appendChild(scriptEl);
    }

    return () => {
      Object.values(metaTags).forEach((el) => el.remove());
      Object.values(linkTags).forEach((el) => el.remove());
      if (scriptEl) scriptEl.remove();
    };
  }, [fullTitle, description, keywords, canonicalUrl, ogType, ogImage, schema, articlePublishedTime, articleModifiedTime, articleAuthor]);

  return null;
}